import { Controller } from '@hotwired/stimulus';

export default class SimulatorV2Controller extends Controller {
  static targets = [
    'amountInput',
    'capitalRepaid',
    'totalGrossInterestReceived',
    'investButton',
  ];

  static values = {
    loanDuration: Number,
    loanAnnualInterestsRate: Number,
    projectCurrency: String,
    projectTranslatedCurrency: String,
    locale: String,
  };

  connect() {
    this.formatter = new Intl.NumberFormat(this.localeValue, {
      style: 'currency',
      currency: this.projectCurrencyValue
    });
    this.#updateDisplay();
  }

  disconnect() {
    clearTimeout(this.debounceTimer);
    clearTimeout(this.gtmTimer);
  }

  update(event) {
    const value = event.target.value;
    if (value.length > 9) {
      event.target.value = value.slice(0, 9);
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.#compute(value), 300);
  }

  reset() {
    setTimeout(() => {
      this.amountInputTarget.value = '';
      this.#updateDisplay();
    }, 100);
  }

  convertToSubscription() {
    document.dispatchEvent(new CustomEvent('simulator:convertToSubscription', {
      detail: { amount: this.amountInputTarget.value }
    }));
  }

  #updateDisplay() {
    this.#compute(this.amountInputTarget.value);
  }

  #compute(value) {
    if (value !== 0 && !value) {
      const emptyValue = this.#formatEmptyValue();
      this.capitalRepaidTarget.value = emptyValue;
      this.totalGrossInterestReceivedTarget.value = emptyValue;
      this.#hideConvertButton();
      return;
    }

    this.capitalRepaidTarget.value = this.formatter.format(value);
    this.totalGrossInterestReceivedTarget.value = this.formatter.format(
      value * this.loanDurationValue * (this.loanAnnualInterestsRateValue / 100) / 12
    );
    this.#showConvertButton();
    this.#scheduleGtmPush();
  }

  #scheduleGtmPush() {
    clearTimeout(this.gtmTimer);
    this.gtmTimer = setTimeout(() => {
      const gtmController = this.application.getControllerForElementAndIdentifier(
        this.amountInputTarget, 'gtm-event'
      );
      gtmController?.appendSubscriptionAmountAndPush({ subscriptionAmount: this.amountInputTarget.value });
    }, 2000);
  }

  #formatEmptyValue() {
    return this.formatter.formatToParts(0)
      .map(({ type, value }) => type === 'currency' || type === 'literal' ? value : '-')
      .join('')
      .replace(/-+/g, '-');
  }

  #hideConvertButton() {
    if (!this.hasInvestButtonTarget) return;
    this.investButtonTarget.disabled = true;
  }

  #showConvertButton() {
    if (!this.hasInvestButtonTarget) return;
    this.investButtonTarget.disabled = false;
  }
}
